import { ReactNode, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ButtonBar, Heading, PopoverDialog, Text, TextLink, usePopoverDialog } from '@frontend/design-system';

type InfoTipPopoverProps = {
  autoOpen?: boolean;
  children: ReactNode | string;
  ctaAction?: () => void;
  ctaTitle?: string;
  title?: string;
  tooltipId: string;
  trackingId?: string;
  onAutoOpen?: () => void;
};

export const InfoTipPopover = ({
  autoOpen,
  children,
  ctaAction,
  ctaTitle,
  title,
  tooltipId,
  trackingId,
  onAutoOpen,
}: InfoTipPopoverProps) => {
  const { t } = useTranslation('analytics');
  const autoOpenHandledRef = useRef(false);

  const { getTriggerProps, getDialogProps, close, open } = usePopoverDialog<HTMLButtonElement>({
    placement: 'bottom',
  });

  const handleCtaClick = () => {
    close();
    ctaAction?.();
  };

  useEffect(() => {
    if (!autoOpen) {
      autoOpenHandledRef.current = false;
      return;
    }
    if (autoOpenHandledRef.current) return;
    autoOpenHandledRef.current = true;
    open();
    onAutoOpen?.();
  }, [autoOpen, open, onAutoOpen]);

  return (
    <>
      <Button
        {...getTriggerProps()}
        className='info-icon-button'
        css={styles.icon}
        iconName='info'
        label='Info'
        trackingId={`info-tip-for-${tooltipId}`}
        variant='secondary'
      />
      <PopoverDialog
        {...getDialogProps()}
        css={css`
          max-height: unset !important;
        `}
      >
        <div css={styles.content} id={`info-tip-popover-content-for-${tooltipId}`}>
          {title && (
            <Heading className='heading' level={3}>
              {title}
            </Heading>
          )}
          {typeof children === 'string' ? <Text>{children}</Text> : children}
          {ctaAction && (
            <ButtonBar css={styles.buttonBar}>
              <TextLink onClick={handleCtaClick} trackingId={`learn-more-about-${trackingId || tooltipId}`}>
                {ctaTitle || t('Learn More')}
              </TextLink>
            </ButtonBar>
          )}
        </div>
      </PopoverDialog>
    </>
  );
};

const styles = {
  icon: css`
    height: ${theme.spacing(3)};
    min-width: ${theme.spacing(3)};
    padding: 0;
    width: ${theme.spacing(3)};

    svg {
      color: ${theme.colors.neutral60};
    }
  `,

  content: css`
    max-width: 400px;
    position: relative;

    .heading {
      margin-bottom: ${theme.spacing(1)};
    }

    p {
      font-size: ${theme.fontSize(14)};
    }
  `,

  buttonBar: css`
    justify-content: flex-start;
    padding: ${theme.spacing(1, 0)};

    button {
      text-decoration: none;

      &:focus {
        text-decoration: none;
      }
    }
  `,
};
